import React from 'react'
import { css, cx } from '@emotion/css'
import {
  theme,
  ThemeColors
} from '@contrast-security-inc/design-system-foundations'
import { Placement } from '../popper'

type ArrowDirection = 'top' | 'bottom' | 'left' | 'right'

const baseTransform = `scale(1.6, 1)`
const arrowDirectionsTransforms: Record<ArrowDirection, string> = {
  top: `rotate(180deg) ${baseTransform}`,
  bottom: baseTransform,
  left: `rotate(90deg) ${baseTransform}`,
  right: `rotate(270deg) ${baseTransform}`
}

type ArrowSize = 'small' | 'large'

const arrowSizeMeasurements: Record<
  ArrowSize,
  {
    readonly lineHeight: number
    readonly lineHeightTopCorrection: number
    readonly lineHeightBottomCorrection: number
  }
> = {
  small: {
    lineHeight: 10,
    lineHeightTopCorrection: 1.5,
    lineHeightBottomCorrection: 2
  },
  large: {
    lineHeight: 20,
    lineHeightTopCorrection: 3,
    lineHeightBottomCorrection: 4
  }
}

function arrowSizeToHeight(size: ArrowSize) {
  const { lineHeight, lineHeightTopCorrection, lineHeightBottomCorrection } =
    arrowSizeMeasurements[size]
  return lineHeight - lineHeightTopCorrection - lineHeightBottomCorrection
}

export const largeArrowHeight = arrowSizeToHeight('large')
export const smallArrowHeight = arrowSizeToHeight('small')
export const largeArrowWithBorderHeight = arrowSizeToHeight('large') + 1
export const smallArrowWithBorderHeight = arrowSizeToHeight('small') + 1

interface Props {
  readonly placement: Placement
  readonly arrowRef: React.Ref<any>
  readonly style: React.CSSProperties
  readonly size: ArrowSize
  readonly color: ThemeColors
  readonly borderColor?: ThemeColors
  readonly className?: string
  readonly isDropdownArrow?: boolean
}

export function Arrow({
  arrowRef,
  style,
  placement,
  size,
  color,
  borderColor,
  isDropdownArrow = false
}: Props) {
  const { lineHeight, lineHeightTopCorrection } = arrowSizeMeasurements[size]
  const arrowMargin = lineHeight - lineHeightTopCorrection
  const arrowCss = css`
    position: absolute;
    pointer-events: none;
    font-size: ${lineHeight}px;
    line-height: 100%;

    &::before {
      content: '▲';
    }

    &[data-popper-placement*='bottom'],
    &[data-popper-placement*='top'] {
      &[data-popper-placement*='start'] {
        margin-left: -4px;
      }
      &[data-popper-placement*='end'] {
        margin-left: 4px;
      }
    }

    &[data-popper-placement*='left'],
    &[data-popper-placement*='right'] {
      &[data-popper-placement*='start'] {
        margin-top: -4px;
      }
      &[data-popper-placement*='end'] {
        margin-top: 4px;
      }
    }

    &[data-popper-placement*='bottom'] {
      top: 0;
      left: 0;
      margin-top: -${arrowMargin + (isDropdownArrow ? 1 : 0)}px;
      transform: ${arrowDirectionsTransforms.bottom};
    }

    &[data-popper-placement*='top'] {
      bottom: 0;
      left: 0;
      margin-bottom: -${arrowMargin}px;
      transform: ${arrowDirectionsTransforms.top};
    }

    &[data-popper-placement*='right'] {
      left: 0;
      margin-left: -${arrowMargin}px;
      transform: ${arrowDirectionsTransforms.right};
    }

    &[data-popper-placement*='left'] {
      right: 0;
      margin-right: -${arrowMargin}px;
      transform: ${arrowDirectionsTransforms.left};
    }
  `

  return (
    <span
      ref={arrowRef}
      style={style}
      data-popper-placement={placement}
      className={cx(
        css`
          label: arrow-outer;
          position: absolute;
          color: transparent;

          &[data-popper-placement*='bottom'],
          &[data-popper-placement*='top'] {
            width: ${lineHeight}px;
          }

          &[data-popper-placement*='right'],
          &[data-popper-placement*='left'] {
            height: ${lineHeight}px;
          }

          &[data-popper-placement*='bottom'] {
            top: 0;
            left: 0;
          }

          &[data-popper-placement*='top'] {
            bottom: 0;
            left: 0;
          }

          &[data-popper-placement*='right'] {
            left: 0;
          }

          &[data-popper-placement*='left'] {
            right: 0;
          }
        `
      )}
    >
      {borderColor && (
        <span
          data-popper-placement={placement}
          className={cx(
            arrowCss,
            css`
              label: arrow-inner-a;
              &::before {
                color: ${theme.color[borderColor]};
              }

              &[data-popper-placement*='bottom'] {
                margin-top: -${arrowMargin + 1}px;
              }

              &[data-popper-placement*='top'] {
                margin-bottom: -${arrowMargin + 1}px;
              }

              &[data-popper-placement*='right'] {
                margin-left: -${arrowMargin + 1}px;
              }

              &[data-popper-placement*='left'] {
                margin-right: -${arrowMargin + 1}px;
              }
            `
          )}
        />
      )}
      <span
        data-popper-placement={placement}
        className={cx(
          arrowCss,
          css`
            label: arrow-inner-b;
            &::before {
              color: ${theme.color[color]};
              text-shadow: 0px -2px 1px rgba(191, 191, 191, 0.3);
            }
          `
        )}
      />
    </span>
  )
}
